import type { JSX } from 'react'
import { formatNextRun } from '../../utils/cron-preview.js'
import Select from '../ui/Select.js'

/**
 * Schedule shape for time-driven automation rules and the broader UI-6a
 * scheduler page.
 *
 * Three modes:
 *   - 'once': fire a single time at a fixed UTC datetime.
 *   - 'interval': fire every N units (minutes/hours/days).
 *   - 'cron': raw cron expression (5 or 6 fields).
 *
 * Persistence note (UI-2c): the @kc3/api `automation-rules` CreateBodySchema
 * does NOT yet carry a `schedule` column — scheduled rules are surfaced into
 * the engine via AutomationEngine.scheduleRule() which writes to the
 * `automation_scheduler` table. Group B's AutomationRulesPage will own the
 * persistence wiring; this component is a controlled editor that emits the
 * structured value for whichever persistence path the caller chooses.
 */
export type Schedule =
  | { mode: 'once'; runAt: string /* ISO 8601 */ }
  | {
      mode: 'interval'
      every: number
      unit: 'minute' | 'hour' | 'day'
    }
  | { mode: 'cron'; expression: string }

export interface ScheduleEditorProps {
  value: Schedule | null
  onChange: (next: Schedule | null) => void
  /**
   * When true, render a "Next run: YYYY-MM-DD HH:mm UTC" line below the
   * editor (cron mode only). Used by the UI-6a Scheduled Reports drawer.
   */
  nextRunPreview?: boolean
}

type Mode = 'once' | 'interval' | 'cron'

// 5- or 6-field cron expression — each field accepts digits, *, /, ',' or '-'.
const CRON_REGEX = /^[\d*/,-]+(?:\s+[\d*/,-]+){4,5}$/

const CRON_PRESETS: ReadonlyArray<{ label: string; cron: string }> = [
  { label: 'Every 5 minutes', cron: '*/5 * * * *' },
  { label: 'Every 15 minutes', cron: '*/15 * * * *' },
  { label: 'Hourly', cron: '0 * * * *' },
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Daily at midnight UTC', cron: '0 0 * * *' },
  { label: 'Weekly on Sunday at midnight UTC', cron: '0 0 * * 0' },
]

function defaultForMode(mode: Mode): Schedule {
  switch (mode) {
    case 'once':
      return { mode: 'once', runAt: '' }
    case 'interval':
      return { mode: 'interval', every: 1, unit: 'hour' }
    case 'cron':
      return { mode: 'cron', expression: '' }
  }
}

function isValidCron(expression: string): boolean {
  return CRON_REGEX.test(expression.trim())
}

function isFutureIso(iso: string): boolean {
  if (!iso) return false
  const t = Date.parse(iso)
  if (Number.isNaN(t)) return false
  return t > Date.now()
}

/**
 * Controlled schedule editor — parent owns state (parallel to RuleBuilder).
 * Not wrapped in a ZodForm (per task spec §0.F note): structured nested
 * editor stays a controlled component so the parent ZodForm can validate
 * the parent rule shape end-to-end.
 */
export default function ScheduleEditor({
  value,
  onChange,
  nextRunPreview = false,
}: ScheduleEditorProps): JSX.Element {
  const mode: Mode = value?.mode ?? 'cron'

  function setMode(next: Mode): void {
    if (value && value.mode === next) return
    onChange(defaultForMode(next))
  }

  function clear(): void {
    onChange(null)
  }

  const cronExpression =
    value && value.mode === 'cron' ? value.expression : ''
  const cronInvalid =
    value?.mode === 'cron' && cronExpression !== '' && !isValidCron(cronExpression)

  const intervalEvery =
    value && value.mode === 'interval' ? value.every : 1
  const intervalUnit =
    value && value.mode === 'interval' ? value.unit : 'hour'
  const intervalInvalid =
    value?.mode === 'interval' && (!Number.isFinite(intervalEvery) || intervalEvery <= 0)

  const onceRunAt = value && value.mode === 'once' ? value.runAt : ''
  const onceInPast = value?.mode === 'once' && onceRunAt !== '' && !isFutureIso(onceRunAt)

  return (
    <div
      className="schedule-editor flex flex-col gap-2"
      role="group"
      aria-label="Schedule editor"
    >
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-700">Schedule mode</label>
                <Select
          ariaLabel="Schedule mode"
          size="sm"
          value={value === null ? '' : mode}
          onChange={(v) => {
            if (v === '') {
              clear()
            } else {
              setMode(v as Mode)
            }
          }}
        >
          <option value="">— none —</option>
          <option value="cron">Cron</option>
          <option value="interval">Interval</option>
          <option value="once">Once</option>
                </Select>
      </div>

      {value === null && (
        <p className="text-xs text-gray-500">No schedule configured.</p>
      )}

      {value?.mode === 'cron' && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-700" htmlFor="schedule-cron">
            Cron expression
          </label>
          <div className="flex items-center gap-2">
            <input
              id="schedule-cron"
              type="text"
              aria-label="Cron expression"
              aria-invalid={cronInvalid || undefined}
              placeholder="0 * * * *"
              value={cronExpression}
              onChange={(e) =>
                onChange({ mode: 'cron', expression: e.target.value })
              }
              className="rounded border border-gray-300 px-1 py-0.5 text-xs"
            />
                        <Select
              ariaLabel="Cron preset"
              size="sm"
              value=""
              onChange={(v) => {
                if (v)
                  onChange({ mode: 'cron', expression: v })
              }}
            >
              <option value="">— preset —</option>
              {CRON_PRESETS.map((p) => (
                <option key={p.cron} value={p.cron}>
                  {p.label}
                </option>
              ))}
                        </Select>
          </div>
          {cronInvalid && (
            <p role="alert" className="text-xs text-red-600">
              Invalid cron expression — expected 5 or 6 fields.
            </p>
          )}
        </div>
      )}

      {value?.mode === 'interval' && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-700">Every</label>
          <input
            type="number"
            min={1}
            aria-label="Interval value"
            aria-invalid={intervalInvalid || undefined}
            value={intervalEvery}
            onChange={(e) => {
              const n = Number(e.target.value)
              onChange({
                mode: 'interval',
                every: Number.isFinite(n) ? n : 0,
                unit: intervalUnit,
              })
            }}
            className="w-20 rounded border border-gray-300 px-1 py-0.5 text-xs"
          />
                    <Select
            ariaLabel="Interval unit"
            size="sm"
            value={intervalUnit}
            onChange={(v) => onChange({
                mode: 'interval',
                every: intervalEvery,
                unit: v as 'minute' | 'hour' | 'day',
              })}
          >
            <option value="minute">minute(s)</option>
            <option value="hour">hour(s)</option>
            <option value="day">day(s)</option>
                    </Select>
          {intervalInvalid && (
            <p role="alert" className="text-xs text-red-600">
              Interval must be greater than zero.
            </p>
          )}
        </div>
      )}

      {value?.mode === 'once' && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-700" htmlFor="schedule-once">
            Run at (UTC)
          </label>
          <input
            id="schedule-once"
            type="datetime-local"
            aria-label="Run at"
            value={onceRunAt}
            onChange={(e) =>
              onChange({ mode: 'once', runAt: e.target.value })
            }
            className="rounded border border-gray-300 px-1 py-0.5 text-xs"
          />
          {onceInPast && (
            <p role="status" className="text-xs text-amber-600">
              Selected datetime is in the past — the schedule will not fire.
            </p>
          )}
        </div>
      )}

      {nextRunPreview && value?.mode === 'cron' && cronExpression !== '' && (
        <p
          aria-label="Next run"
          className="text-xs text-gray-600"
        >
          Next run: {formatNextRun(cronExpression)}
        </p>
      )}
    </div>
  )
}
